import { createStyles, makeStyles, Paper, Typography } from '@material-ui/core';
import { WarningStatus } from '../../icons/StatusIcons';
import { NetworkPathType } from '../../utils/getNetworkPathType';

const useStyles = makeStyles((theme) =>
  createStyles({
    notice: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75em',
      padding: '1em',
      marginTop: '1.5em',
      maxWidth: '640px',
      borderRadius: '8px',
      backgroundColor: '#FDF4E7',
      border: '1px solid #F5A623',
      [theme.breakpoints.down('md')]: {
        clear: 'both',
      },
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      marginTop: '2px',
    },
    heading: {
      marginBottom: '0.25em',
    },
  })
);

interface RestrictiveNetworkNoticeProps {
  pathType: NetworkPathType;
}

/**
 * A non-blocking advisory shown when the preflight test connected via a TURN
 * relay rather than a direct path. A relayed path often indicates a VPN,
 * corporate firewall, or strict NAT is in play, any of which can degrade a
 * live video (notarization) session. This never blocks progress — connectivity
 * technically succeeded — it only informs the user so they can improve their
 * setup before the appointment.
 *
 * The wording is intentionally softer for a UDP relay (common on cellular /
 * carrier-grade NAT) than for a TCP/TLS relay (which almost always means UDP is
 * being actively blocked, e.g. by a VPN or firewall).
 */
export function RestrictiveNetworkNotice({ pathType }: RestrictiveNetworkNoticeProps) {
  const classes = useStyles();

  if (pathType !== 'relayed-udp' && pathType !== 'relayed-tcp') {
    return null;
  }

  const isBlockingUdp = pathType === 'relayed-tcp';

  return (
    <Paper elevation={0} className={classes.notice} data-testid="restrictive-network-notice">
      <span className={classes.iconContainer}>
        <WarningStatus />
      </span>
      <div>
        <Typography variant="body1" className={classes.heading}>
          <strong>Restrictive network detected</strong>
        </Typography>
        <Typography variant="body1">
          {isBlockingUdp
            ? `Your connection required a relay server because direct video traffic appears to be blocked. This is commonly caused by a VPN, corporate firewall, or strict network security settings and may increase the risk of call issues. If you're using a VPN, consider disabling it, or switch to a less restricted network before your appointment.`
            : `Your connection required a relay server to complete this test. This can happen on some home, cellular, or shared networks and may increase the risk of call issues. If possible, connect to a more reliable network before your appointment.`}
        </Typography>
      </div>
    </Paper>
  );
}
